import { Column, Icon, Row, Spinner, Typography } from '@a_team/ui-components';
import {
  BorderColors,
  Breakpoints,
  Colors,
  TextColors,
} from '@ateams/components';
import FileUploader from '@src/components/FileUploader';
import Image from '@src/components/Image';
import MainLayout from '@src/layouts/Main';
import { useStores } from '@src/stores';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { useGetProfilePictureAnalysis } from '@src/rq/profile';
import Actions from './Actions';
import { useGetProfilePictureFromLinkedin } from '@src/rq/users';
import { ProfilePictureValidationLabels } from './ProfilePictureValidationLabels';
import { isDefaultProfileImage } from '../Profile/Sidebar/ProfileImage/utils';
import Skeleton from 'react-loading-skeleton';
import ConfirmModalV2 from '@src/components/Modal/ConfirmModalV2';
import { useAnalytics } from '@ateams/analytics/dist/platform';

const sampleImages = {
  avatar1:
    'https://ucarecdn.com/2446aa09-c51b-4754-978b-3cc9658bcec8/-/preview/999x999/',
  avatar2:
    'https://ucarecdn.com/f673a564-0f3e-42f1-8aad-89ba3c072e6e/-/preview/999x999/',
  avatar3:
    'https://ucarecdn.com/44de3091-8b19-48ff-873e-ae12e4c83d1e/-/preview/999x999/',
  avatar4:
    'https://ucarecdn.com/a9e1cec9-5054-42b4-ba37-a0bce72fda25/-/preview/999x999/',
  avatar5:
    'https://ucarecdn.com/ae3ab9cf-e6ff-4b8e-92d3-3c2f8a59aa6c/-/preview/999x999/',
  avatar6:
    'https://ucarecdn.com/b5656e1f-e7c6-4cf4-b077-3c95d017398d/-/preview/999x999/',
};

interface Props {
  withoutMainLayout?: boolean;
  onClose?: () => void;
  scrollToLinkedInSection?: () => void;
}

interface AvatarExampleProps {
  url: string;
  valid?: boolean;
}

const useStyles = createUseStyles({
  title: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '26px',
    marginBottom: 8,
  },
  subTitles: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '19px',
    color: TextColors.regularLight,
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  lastSubTitle: {
    marginTop: 24,
  },
  uploader: {
    width: 332,
    minHeight: 300,
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  fileDropArea: {
    background: 'none',
    borderRadius: '50% !important',
    width: 300,
    minHeight: 300,
    textAlign: 'center',
    padding: 0,
    '&.error': {
      borderColor: Colors.primary,
    },
  },
  content: {
    height: '100%',
    paddingTop: 0,
    paddingBottom: 0,
    position: 'relative',
  },
  contentWrapper: {
    color: TextColors.regular,
  },
  close: {
    position: 'absolute',
    right: 16,
    top: 40,
  },
  leftCol: {
    borderBottom: `1px solid ${BorderColors.lighter}`,
    padding: 40,
    marginBottom: 40,
    paddingBottom: 50,
  },
  suggestionsTitle: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '26px',
  },
  suggestionsDescription: {
    marginTop: 8,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '19px',
    color: TextColors.regularLight,
    marginBottom: 40,
  },
  avatarRow: {
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap',
  },
  section: {
    marginBottom: 47,
  },
  indication: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    right: -5,
    top: 15,
  },
  avatar: {
    marginTop: 24,
    borderRadius: '16px !important',
  },
  dragAndDropArea: {
    width: '100%',
    marginTop: 40,
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    '& h3': {
      fontSize: 14,
      fontWeight: 400,
    },
    '& input[type="file"] + div > div': {
      background: 'none',
      borderRadius: 16,
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      backdropFilter: 'none',
      padding: 50,
    },
    '& div[class^="errors"]': {
      display: 'none',
    },
  },
  [`@media (min-width: ${Breakpoints.sm}px)`]: {
    close: {
      position: 'absolute',
      right: 16,
      top: 16,
    },
    leftCol: {
      borderRight: `1px solid ${BorderColors.lighter}`,
      borderBottom: 'none',
      paddingBottom: 40,
    },
  },
  spinner: {
    marginRight: 12,
  },
  avatarSkeleton: {
    position: 'relative',
    display: 'inline-block',
    marginTop: 30,
  },
  indicatorSkeleton: {
    position: 'absolute',
    top: -8,
    right: -8,
  },
});

const AvatarExample = (props: AvatarExampleProps) => {
  const styles = useStyles();
  const { valid = true, url } = props;

  return (
    <span style={{ position: 'relative' }}>
      <Image
        width={100}
        height={100}
        sizes="100px"
        src={url}
        className={styles.avatar}
        alt="avatar"
        aspectRatio={1}
      />
      <span
        className={styles.indication}
        style={{
          background: valid ? Colors.secondaryDark : Colors.primary,
        }}
      >
        <Icon
          size={'md'}
          color={'Grey@0'}
          name={valid ? 'statusPositiveNoBorder' : 'statusNegativeNoBorder'}
        />
      </span>
    </span>
  );
};

const ProfileImageViewV2 = ({
  withoutMainLayout,
  onClose,
  scrollToLinkedInSection,
}: Props) => {
  const {
    auth,
    users: { profile },
  } = useStores();
  const styles = useStyles();
  const history = useHistory();
  const analytics = useAnalytics();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const onCloseConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const onDiscardConfirmModal = () => {
    setConfirmModalOpen(false);
    if (onClose) {
      onClose();
      return;
    }

    history.goBack();
  };

  const onSaveFromConfirmModal = () => {
    setConfirmModalOpen(false);
    handleSaveImage();
  };

  const isDefaultImage = useMemo(() => {
    return (
      !auth.user?.profilePictureURL ||
      isDefaultProfileImage(auth.user?.profilePictureURL)
    );
  }, [auth.user?.profilePictureURL]);

  const [tempImage, setTempImage] = useState<string | undefined>(
    !isDefaultImage ? auth.user?.profilePictureURL : undefined,
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  const [
    isImportingLinkedinProfilePicture,
    setIsImportingLinkedinProfilePicture,
  ] = useState(false);

  const { refetch } = useGetProfilePictureFromLinkedin({
    enabled: false,
    onSuccess: (data) => {
      setIsImportingLinkedinProfilePicture(false);
      setTempImage(data?.linkedinProfilePicture);
    },
  });

  useEffect(() => {
    if (!tempImage && auth.user?.profilePictureURL && !isDefaultImage) {
      setTempImage(auth.user?.profilePictureURL);
    }
  }, [setTempImage, tempImage, auth.user?.profilePictureURL, isDefaultImage]);

  const handleSaveImage = () => {
    if (loading) return;

    if (tempImage === auth.user?.profilePictureURL) {
      handleOnClose();
      return;
    }

    setLoading(true);
    if (tempImage) {
      profile?.updateProfileImage(tempImage).then(() => {
        setLoading(false);
        handleOnClose();
      });
    }
  };

  const { data, isLoading: isLoadingPictureAnalysis } =
    useGetProfilePictureAnalysis(tempImage || '', true);

  const issues = data?.issues || [];

  const handleOnClose = () => {
    if (!!tempImage && tempImage !== auth.user?.profilePictureURL) {
      setConfirmModalOpen(true);
      return;
    }

    if (onClose) {
      onClose();
      return;
    }

    history.goBack();
  };

  const closeModalAndScrollToLinkedInSection = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      history.goBack();
    }

    scrollToLinkedInSection?.();
  }, []);

  const handleUploadingChange = (newIsUploading: boolean) => {
    setIsUploading(newIsUploading);
    analytics.trackProfileImageUploadStarted(auth.uid ?? '');
  };

  const showSuccessState =
    !isDefaultImage &&
    !isUploading &&
    !isLoadingPictureAnalysis &&
    data?.issues.length === 0;

  const content = () => {
    return (
      <>
        <div className={styles.contentWrapper}>
          <Icon
            name={'statusNegativeNoBorder'}
            className={styles.close}
            onClick={handleOnClose}
          />
          <Row style={{ height: withoutMainLayout ? 'auto' : '100%' }}>
            <Column className={styles.leftCol}>
              <div className={styles.title}>Upload a profile photo</div>
              <div className={styles.subTitles}>
                <div>Minimum 500x500px and up to 5MB.</div>
                <div>
                  Make sure your face is visible in the highlighted circle.
                </div>
                <div className={styles.lastSubTitle}>
                  PNG and JPEG file formats are supported.
                </div>
              </div>
              <div>
                <div className={styles.dragAndDropArea}>
                  <FileUploader
                    hints={[]}
                    fileUrl={tempImage}
                    onFileUploadSuccess={setTempImage}
                    cropAspect={1}
                    type={'default'}
                    withOptionBox={false}
                    withCrop={true}
                    circularCrop={true}
                    inlineCropping
                    maxFileSize={5242880}
                    minFileDimensions={{
                      width: 500,
                      height: 500,
                    }}
                    className={styles.uploader}
                    fileDropAreaClassName={styles.fileDropArea}
                    onFileUploadingChange={handleUploadingChange}
                    onErrorsChange={(errors) => setErrors(errors)}
                    customButtons={
                      <Actions
                        disabled={
                          isUploading || isImportingLinkedinProfilePicture
                        }
                        onConfirm={handleSaveImage}
                        onLinkedinImport={() => {
                          setIsImportingLinkedinProfilePicture(true);
                          refetch();
                        }}
                        loading={loading}
                        errors={errors}
                        isImportingLinkedinProfilePicture={
                          isImportingLinkedinProfilePicture
                        }
                        disableSaveButton={
                          (isDefaultImage && !tempImage) ||
                          (!!tempImage &&
                            tempImage === auth.user?.profilePictureURL)
                        }
                        noLinkedInAvailable={!auth.user?.linkedIn?.username}
                        closeModalAndScrollToLinkedInSection={
                          closeModalAndScrollToLinkedInSection
                        }
                      />
                    }
                  />
                </div>
              </div>
            </Column>

            <Column style={{ padding: 40 }}>
              {isLoadingPictureAnalysis ? (
                <>
                  <div>
                    <Spinner className={styles.spinner} size="md" />
                    <span className={styles.suggestionsTitle}>
                      Reviewing your profile photo
                    </span>
                  </div>
                  <div className={styles.suggestionsDescription}>
                    Builders with a quality profile photo are more likely to be
                    selected for missions.
                  </div>
                  <div>
                    <Skeleton width={80} />
                    <br />
                    <Skeleton width={400} />
                    <Skeleton width={400} />
                    <div className={styles.avatarSkeleton}>
                      <Skeleton width={100} height={100} borderRadius={16} />
                      <div className={styles.indicatorSkeleton}>
                        <Skeleton
                          width={24}
                          height={24}
                          borderRadius={24}
                          circle={true}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : showSuccessState ? (
                <>
                  <div className={styles.suggestionsTitle}>
                    No suggested improvements
                  </div>
                  <div className={styles.suggestionsDescription}>
                    Your profile photo meets all platform guidelines
                  </div>

                  <div className={styles.section}>
                    <Typography variant={'h3'} margin={'none'}>
                      Your photo looks great
                    </Typography>
                    <Typography
                      variant={'textMedium'}
                      component={'p'}
                      color={'Grey@500'}
                      size={15}
                      weight={'light'}
                    >
                      <div>
                        A clear, professional photo like this helps build trust
                        and increases your chances of getting selected for
                        missions.
                      </div>
                    </Typography>
                    <div className={styles.avatarRow}>
                      <AvatarExample url={tempImage || ''} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.suggestionsTitle}>
                    Suggestions for your uploaded photo
                  </div>
                  {issues.length === 0 ? (
                    <>
                      <div className={styles.suggestionsDescription}>
                        Builders with a quality profile photo are more likely to
                        be selected for missions.
                      </div>
                      <div className={styles.section}>
                        <Typography variant={'h3'} margin={'none'}>
                          Background
                        </Typography>
                        <Typography
                          variant={'textMedium'}
                          component={'p'}
                          color={'Grey@500'}
                          size={15}
                          weight={'light'}
                        >
                          <div>
                            A plain background is often the best choice.
                          </div>
                          <div>
                            If you choose a busier background, make sure that
                            it's blurred so you remain the focal point.
                          </div>
                        </Typography>
                        <div className={styles.avatarRow}>
                          <AvatarExample url={sampleImages.avatar1} />
                          <AvatarExample url={sampleImages.avatar2} />
                        </div>
                      </div>
                      <div className={styles.section}>
                        <Typography variant={'h3'} margin={'none'}>
                          Minimize distractions
                        </Typography>
                        <Typography
                          variant={'textMedium'}
                          component={'p'}
                          color={'Grey@500'}
                          size={15}
                          weight={'light'}
                        >
                          Avoid accessories like headphones and anything that
                          obscures your face, such as hats or sunglasses. Ensure
                          that only you are in the photo to maintain focus on
                          your profile.
                        </Typography>
                        <div className={styles.avatarRow}>
                          <AvatarExample url={sampleImages.avatar3} />
                          <AvatarExample url={sampleImages.avatar4} />
                        </div>
                      </div>
                      <div className={styles.section}>
                        <Typography variant={'h3'} margin={'none'}>
                          Image clarity
                        </Typography>
                        <Typography
                          variant={'textMedium'}
                          component={'p'}
                          weight={'light'}
                          color={'Grey@500'}
                          size={15}
                        >
                          Ensure your picture is of high resolution and sharp.
                        </Typography>
                        <div className={styles.avatarRow}>
                          <AvatarExample url={sampleImages.avatar5} />
                          <AvatarExample url={sampleImages.avatar6} />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.suggestionsDescription}>
                        Please consider the following suggestions to improve
                        your profile picture.
                      </div>
                      {issues.map((issue) => {
                        const label = ProfilePictureValidationLabels[issue];
                        if (!label) return null;
                        return (
                          <div className={styles.section}>
                            <Typography variant={'h3'} margin={'none'}>
                              <div>{label.title}</div>
                            </Typography>
                            <Typography
                              variant={'textMedium'}
                              component={'p'}
                              color={'Grey@500'}
                              size={15}
                              weight={'light'}
                            >
                              <div>{label.description}</div>
                            </Typography>
                            <div className={styles.avatarRow}>
                              {label.images.map((image) => (
                                <AvatarExample
                                  key={image}
                                  url={image}
                                  valid={false}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </Column>
          </Row>
        </div>

        <ConfirmModalV2
          title="Do you want to save the changes?"
          description="If you exit, your changes to the photo will be lost."
          open={confirmModalOpen}
          onClose={onCloseConfirmModal}
          onCancel={onDiscardConfirmModal}
          onCancelText="Discard"
          onConfirm={onSaveFromConfirmModal}
          onConfirmText="Save photo"
          onConfirmColor="secondaryDark"
        />
      </>
    );
  };

  if (withoutMainLayout) {
    return <>{content()}</>;
  }

  return (
    <MainLayout
      title={'Profile Image'}
      style={{ backgroundColor: 'white' }}
      contentClassName={styles.content}
    >
      {content()}
    </MainLayout>
  );
};

export default observer(ProfileImageViewV2);
